import React, { useState, useEffect, useRef, MouseEventHandler, KeyboardEventHandler, useCallback } from "react";
import { CSSTransition } from "react-transition-group";

import useSearchResults from "lib/global_search/useSearchResults";

import styles from "./search.module.css";

import SearchResults from "./SearchResults";
import SearchStartState from "./SearchStartState";

type LoadingIndicatorProps = {
  loading: boolean;
};
const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({ loading }) => {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <CSSTransition nodeRef={ref} in={loading} timeout={200} unmountOnExit classNames={styles}>
      <div className={styles.loadingIndicator} ref={ref} />
    </CSSTransition>
  );
};

type SearchBoxProps = {
  focus: boolean;
  handleClose: MouseEventHandler;
};

const SearchBox: React.FC<SearchBoxProps> = ({ focus, handleClose }) => {
  const el = useRef<HTMLDivElement>(null);
  const inputEl = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const [searchInput, setSearchInput] = useState("");
  const { searchResults, loading } = useSearchResults(searchInput);
  const [currentlyFocussedId, setCurrentlyFocussedId] = useState<string | null | undefined>(null);

  const getFocussableNodes: () => HTMLElement[] = useCallback(() => {
    return Array.from(el.current?.querySelectorAll("[data-hoverable-id]") || []) as HTMLElement[];
  }, []);

  const handleMouseMove: MouseEventHandler = useCallback((event) => {
    const target = event.target as Element;
    const hoveredFocussableEl = target.closest("[data-hoverable-id]");

    if (hoveredFocussableEl && hoveredFocussableEl instanceof HTMLElement) {
      setCurrentlyFocussedId(hoveredFocussableEl.dataset.hoverableId);
    }
  }, []);

  useEffect(() => {
    if (focus) {
      setSearchInput("");
      inputEl.current?.focus();
    }
  }, [focus]);

  useEffect(() => {
    if (focus && !currentlyFocussedId) {
      setCurrentlyFocussedId(getFocussableNodes()[0].dataset.hoverableId);
    }
  }, [focus, currentlyFocussedId, getFocussableNodes]);

  useEffect(() => {
    const nodes = getFocussableNodes();

    if (nodes.length === 0) return;

    setCurrentlyFocussedId(nodes[0].dataset.hoverableId);
  }, [getFocussableNodes, searchResults]);

  const handleKeydown: KeyboardEventHandler = useCallback(
    (event) => {
      const nodes = getFocussableNodes();
      const currentIndex = nodes.findIndex(({ dataset: { hoverableId } }) => hoverableId === currentlyFocussedId);

      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (nodes[currentIndex + 1]) {
          setCurrentlyFocussedId(nodes[currentIndex + 1].dataset.hoverableId);
        }
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        if (nodes[currentIndex - 1]) {
          setCurrentlyFocussedId(nodes[currentIndex - 1].dataset.hoverableId);
        }
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (nodes[currentIndex]) {
          nodes[currentIndex].click();
        }
      }
    },
    [currentlyFocussedId, getFocussableNodes]
  );

  return (
    <CSSTransition
      in={focus}
      timeout={200}
      unmountOnExit
      nodeRef={searchBoxRef}
      classNames={{
        enter: styles.searchBoxEnter,
        enterActive: styles.searchBoxEnterActive,
        exit: styles.searchBoxExit,
        exitActive: styles.searchBoxExitActive,
      }}
    >
      <div className={styles.searchBox} onClick={handleClose} ref={searchBoxRef}>
        <div
          className={styles.actualSearchBox}
          onClick={(event) => event.stopPropagation()}
          onMouseMove={handleMouseMove}
          ref={el}
        >
          <form action="" className={styles.form}>
            <input
              type="text"
              id="search-box-input"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={styles.searchInput}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Search..."
              onKeyDown={handleKeydown}
              ref={inputEl}
            />
            <LoadingIndicator loading={loading} />
          </form>
          <div className={styles.searchResults}>
            {searchInput.length === 0 && !loading ? (
              <SearchStartState currentlyFocussedId={currentlyFocussedId} setSearchInput={setSearchInput} />
            ) : (
              <SearchResults
                searchResults={searchResults}
                currentlyFocussedId={currentlyFocussedId}
                searchInput={searchInput}
              />
            )}
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

export default SearchBox;
